/**
 * Google Maps Service
 * 
 * Generates URLs for Google Maps embed and directions.
 * 
 * Requirements:
 * - 7.1: Display interactive map at restaurant location
 */

import { GOOGLE_MAPS_API_KEY, RESTAURANT_INFO } from '../utils/constants';

/**
 * Generates a Google Maps embed URL for displaying a map
 * 
 * @param address - Full address to display on the map
 * @param apiKey - Google Maps API key
 * @returns Embed URL for iframe
 * 
 * Supports requirement 7.1: Display interactive map at restaurant location
 */
export const generateMapEmbedUrl = (
  address: string = RESTAURANT_INFO.address,
  apiKey: string = GOOGLE_MAPS_API_KEY
): string => {
  if (!apiKey) {
    return '';
  }

  const encodedAddress = encodeURIComponent(address);
  return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodedAddress}&zoom=15`;
};

/**
 * Generates a Google Maps directions URL
 * Opens in a new tab to provide directions to the restaurant
 * 
 * @param address - Destination address
 * @returns URL for Google Maps directions
 * 
 * Supports requirement 7.2: Provide "Route planen" button
 */
export const generateDirectionsUrl = (
  address: string = RESTAURANT_INFO.address
): string => {
  const encodedAddress = encodeURIComponent(address);
  return `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
};

/**
 * Generates a static Google Maps URL (fallback when API key is not available)
 * 
 * @param address - Address to display
 * @returns URL for static Google Maps link
 */
export const generateStaticMapUrl = (
  address: string = RESTAURANT_INFO.address
): string => {
  const encodedAddress = encodeURIComponent(address);
  return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
};
